import { useState, useEffect } from "react";
import { useApp } from "@/context/AppContext";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Lock } from "lucide-react";

export default function Checkout() {
  const { cart, cartSubtotal, clearCart, settings } = useApp();
  const navigate = useNavigate();
  const [ship, setShip] = useState({ full_name: "", phone: "", email: "", address_line1: "", address_line2: "", city: "", state: "", pincode: "", country: "India" });
  const [shipCost, setShipCost] = useState(99);
  const [placing, setPlacing] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [customerLoading, setCustomerLoading] = useState(true);

  useEffect(() => {
    api.get("/customer/me")
      .then((response) => {
        const signedIn = response.data.customer;
        setCustomer(signedIn);
        setShip((current) => ({
          ...current,
          full_name: signedIn.full_name || current.full_name,
          phone: signedIn.phone || current.phone,
          email: signedIn.email || current.email,
        }));
      })
      .catch(() => {})
      .finally(() => setCustomerLoading(false));
  }, []);

  useEffect(() => {
    if (!cart.length) return;
    const t = setTimeout(() => {
      api.post("/shipping/quote", { subtotal: cartSubtotal, pincode: ship.pincode }).then((r) => setShipCost(r.data.shipping_cost)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [ship.pincode, cartSubtotal, cart.length]);

  if (!cart.length) {
    return <div className="max-w-3xl mx-auto p-10 text-center">
      <h1 className="font-display text-3xl mb-4">Your cart is empty</h1>
      <p className="text-white/60">Add some products before checking out.</p>
    </div>;
  }

  if (customerLoading) {
    return <div className="max-w-3xl mx-auto p-10 text-center text-white/60">Checking your account...</div>;
  }

  if (!customer) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center" data-testid="checkout-account-required">
        <div className="overline text-[#E63946] mb-3">Checkout</div>
        <h1 className="font-display text-3xl mb-3">SIGN IN TO PLACE YOUR ORDER</h1>
        <p className="text-white/60 mb-6">Your order status and complete order details will be saved to your account.</p>
        <Link to="/account" state={{ returnTo: "/checkout" }} className="sa-btn sa-btn-primary">SIGN IN OR CREATE ACCOUNT</Link>
      </div>
    );
  }

  const total = cartSubtotal + shipCost;

  const loadRazorpayScript = () => new Promise((res) => {
    if (window.Razorpay) return res(true);
    const s = document.createElement("script"); s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => res(true); s.onerror = () => res(false);
    document.body.appendChild(s);
  });

  const placeOrder = async (e) => {
    e.preventDefault();
    setPlacing(true);
    try {
      const orderRes = await api.post("/orders", {
        items: cart, shipping: ship, subtotal: cartSubtotal, shipping_cost: shipCost, total,
      });
      const { order, razorpay_order_id, razorpay_key_id, amount, mock_mode } = orderRes.data;

      if (mock_mode) {
        // Simulate successful payment for demo
        const mockPaymentId = "pay_mock_" + Math.random().toString(36).slice(2, 14);
        await api.post("/orders/verify", {
          order_id: order.id,
          razorpay_order_id,
          razorpay_payment_id: mockPaymentId,
          razorpay_signature: "mock_signature",
        });
        clearCart();
        toast.success("Order placed! (Demo mode — add Razorpay keys in admin to enable real payments)");
        navigate(`/order/${order.order_number}`);
        return;
      }

      const ok = await loadRazorpayScript();
      if (!ok) { toast.error("Failed to load payment gateway."); setPlacing(false); return; }
      const rp = new window.Razorpay({
        key: razorpay_key_id,
        amount, currency: "INR",
        name: "Street Auto Accessories",
        description: `Order ${order.order_number}`,
        order_id: razorpay_order_id,
        prefill: { name: ship.full_name, email: ship.email, contact: ship.phone },
        theme: { color: "#E63946" },
        handler: async (resp) => {
          await api.post("/orders/verify", {
            order_id: order.id,
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          clearCart();
          toast.success("Payment successful!");
          navigate(`/order/${order.order_number}`);
        },
      });
      rp.open();
    } catch (err) {
      toast.error("Could not place order.");
    } finally { setPlacing(false); }
  };

  const setField = (k, v) => setShip((s) => ({ ...s, [k]: v }));

  return (
    <div data-testid="checkout-page" className="max-w-[1400px] mx-auto px-4 md:px-8 py-10 md:py-14">
      <div className="grid md:grid-cols-[1fr_420px] gap-8">
        <form onSubmit={placeOrder} className="space-y-6">
          <div>
            <div className="overline text-[#E63946] mb-3">Checkout</div>
            <h1 className="font-display text-3xl md:text-5xl tracking-tighter">SHIPPING DETAILS</h1>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input required placeholder="Full Name" className="sa-input" value={ship.full_name} onChange={(e) => setField("full_name", e.target.value)} data-testid="ship-name" />
            <input required placeholder="Phone" className="sa-input" value={ship.phone} onChange={(e) => setField("phone", e.target.value)} data-testid="ship-phone" />
            <input required type="email" placeholder="Email" className="sa-input sm:col-span-2" value={ship.email} onChange={(e) => setField("email", e.target.value)} data-testid="ship-email" />
            <input required placeholder="Address Line 1" className="sa-input sm:col-span-2" value={ship.address_line1} onChange={(e) => setField("address_line1", e.target.value)} data-testid="ship-addr1" />
            <input placeholder="Address Line 2 (optional)" className="sa-input sm:col-span-2" value={ship.address_line2} onChange={(e) => setField("address_line2", e.target.value)} />
            <input required placeholder="City" className="sa-input" value={ship.city} onChange={(e) => setField("city", e.target.value)} data-testid="ship-city" />
            <input required placeholder="State" className="sa-input" value={ship.state} onChange={(e) => setField("state", e.target.value)} data-testid="ship-state" />
            <input required placeholder="Pincode" className="sa-input" value={ship.pincode} onChange={(e) => setField("pincode", e.target.value)} data-testid="ship-pincode" />
            <input placeholder="Country" className="sa-input" value={ship.country} onChange={(e) => setField("country", e.target.value)} />
          </div>

          <div className="bg-[#111] border border-white/10 p-5 text-sm text-white/70 flex items-start gap-3">
            <Lock size={16} className="text-[#E63946] mt-0.5 flex-shrink-0"/>
            <div>
              Online payment only (via Razorpay). <b className="text-white">Cash on Delivery is currently unavailable.</b> Your payment is processed securely.
            </div>
          </div>
        </form>

        <aside className="bg-[#111] border border-white/10 p-6 h-fit md:sticky md:top-20 space-y-4">
          <div className="overline text-white/60">Order Summary</div>
          <ul className="divide-y divide-white/5 max-h-[300px] overflow-auto">
            {cart.map((i) => (
              <li key={i.product_id} className="py-3 flex items-center gap-3">
                <div className="w-14 h-14 bg-black flex-shrink-0">{i.image && <img src={i.image} className="w-full h-full object-cover" alt="" />}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm line-clamp-2">{i.name}</div>
                  <div className="text-xs text-white/50">Qty {i.quantity}</div>
                </div>
                <div className="text-sm">{formatINR(i.price * i.quantity)}</div>
              </li>
            ))}
          </ul>
          <div className="border-t border-white/10 pt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-white/60">Subtotal</span><span>{formatINR(cartSubtotal)}</span></div>
            <div className="flex justify-between"><span className="text-white/60">Shipping</span><span>{shipCost === 0 ? "FREE" : formatINR(shipCost)}</span></div>
            <div className="flex justify-between font-display text-xl mt-3"><span>TOTAL</span><span>{formatINR(total)}</span></div>
            <div className="text-xs text-white/50">Estimated delivery: {settings?.shipping_estimate_days || "3-7 working days"}</div>
          </div>
          <button onClick={placeOrder} disabled={placing} className="sa-btn sa-btn-primary w-full py-4" data-testid="place-order">
            <ShieldCheck size={16} /> {placing ? "PROCESSING..." : "PLACE ORDER"}
          </button>
        </aside>
      </div>
    </div>
  );
}
